/**
 * Contact Page - UPDATED
 * Multiple contact categories for GetWordExpress
 * ✅ ADDED: Support, Suggestions, Billing, Hello email options
 * ✅ ADDED: Link to suggestion voting system
 */

import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PaymentIcon from '@mui/icons-material/Payment';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'support',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const contactCategories = [
    {
      id: 'support',
      title: 'Technical Support',
      icon: BugReportIcon,
      email: 'support@getwordexpress.com',
      description: 'Bugs, account issues, technical problems',
      color: '#667eea',
      action: 'email'
    },
    {
      id: 'suggestions',
      title: 'Feature Ideas',
      icon: LightbulbIcon,
      email: 'suggestions@getwordexpress.com',
      description: 'Share your ideas and vote on features!',
      color: '#f59e0b',
      action: 'page', // Special: goes to suggestions page
      badge: '🔥 New!'
    },
    {
      id: 'billing',
      title: 'Billing Questions',
      icon: PaymentIcon,
      email: 'billing@getwordexpress.com',
      description: 'Payments, unlocks, refunds',
      color: '#10b981',
      action: 'email'
    },
    {
      id: 'hello',
      title: 'General Inquiries',
      icon: WavingHandIcon,
      email: 'hello@getwordexpress.com',
      description: 'Partnerships, press, or just say hi!',
      color: '#ec4899',
      action: 'email'
    }
  ];

  const handleCategoryClick = (category) => {
    if (category.action === 'page') {
      // Navigate to suggestions page
      navigate('/suggestions');
    } else {
      // Set form category and scroll to form
      setFormData({ ...formData, category: category.id });
      document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    setSending(true);
    setError('');
    setSuccess(false);

    try {
      // TODO: Replace with actual API endpoint
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({ 
          name: '', 
          email: '', 
          category: 'support',
          subject: '', 
          message: '' 
        });
      } else {
        throw new Error('Failed to send message');
      }
      
    } catch (err) {
      setError('Failed to send message. Please try emailing us directly at ' + 
        contactCategories.find(c => c.id === formData.category)?.email);
    } finally {
      setSending(false);
    }
  };

  const selectedCategory = contactCategories.find(c => c.id === formData.category);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Get In Touch
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Choose how you'd like to reach us
          </Typography>
        </Box>

        {/* Contact Category Cards */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {contactCategories.map((category) => {
            const IconComponent = category.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={category.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleCategoryClick(category)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      {category.badge && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: '#fef3c7',
                            color: '#f59e0b',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {category.badge}
                        </Box>
                      )}
                      
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: `${category.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <IconComponent 
                          sx={{ 
                            fontSize: 40, 
                            color: category.color 
                          }} 
                        />
                      </Box>

                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        gutterBottom
                        sx={{ color: category.color }}
                      >
                        {category.title}
                      </Typography>

                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 2, minHeight: 40 }}
                      >
                        {category.description}
                      </Typography>

                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: category.color,
                          fontWeight: 600,
                          display: 'block'
                        }}
                      >
                        {category.action === 'page' ? 'Visit Page →' : category.email}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Special Callout for Suggestions */}
        <Alert 
          severity="info" 
          icon={<LightbulbIcon />}
          sx={{ 
            mb: 6,
            bgcolor: '#fef3c7',
            color: '#92400e',
            border: '2px solid #fbbf24',
            '& .MuiAlert-icon': {
              color: '#f59e0b'
            }
          }}
        >
          <Typography variant="body1" fontWeight="600" gutterBottom>
            💡 Help Us Build What YOU Want!
          </Typography>
          <Typography variant="body2">
            Have a feature idea? Visit our <strong><a href="/suggestions" style={{ color: '#f59e0b', textDecoration: 'none' }}>Suggestions Page</a></strong> to submit your ideas and vote on what we build next! 
            Every month, we implement the top community-voted feature. Your voice shapes our roadmap!
          </Typography>
        </Alert>

        {/* Contact Form */}
        <Paper id="contact-form" sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {selectedCategory && (
              <>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: `${selectedCategory.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <selectedCategory.icon sx={{ color: selectedCategory.color }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {selectedCategory.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCategory.email}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {success && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />} 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(false)}
            >
              Message sent successfully! We'll respond within 24 hours.
            </Alert>
          )}

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Your Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={sending}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={sending}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={sending}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {contactCategories.filter(c => c.action === 'email').map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={sending}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  required
                  disabled={sending}
                  placeholder="Tell us how we can help..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={sending}
                  startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${selectedCategory?.color} 0%, ${selectedCategory?.color}dd 100%)`,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f9ff', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              💡 <strong>Quick Tip:</strong> For technical support, please include your account email and generation ID if applicable. We typically respond within 24 hours during business days.
            </Typography>
          </Box>
        </Paper>

        {/* Additional Info */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Looking for help? Check out our <a href="/faq" style={{ color: '#667eea', textDecoration: 'none' }}>FAQ</a> or <a href="/privacy" style={{ color: '#667eea', textDecoration: 'none' }}>Privacy Policy</a>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            🕐 Response Time: Within 24 hours • Business Hours: Mon-Fri, 9am-6pm EST
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;